import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, redirect, useFetcher, useLoaderData } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@marketplace/ui';
import {
  type CalloutReleaseView,
  type HandshakeIssued,
  type HandshakeState,
} from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { HANDSHAKE_POLL_MS, handshakeRefusalKey } from '../features/jobs/handshake.js';
import { HandshakeQr } from '../features/jobs/HandshakeQr.js';
import { loadAwardedQuotes } from '../features/jobs/work-query.js';
import { usePoll } from '../features/messages/poll.js';
import { loadCalloutRelease } from '../features/payouts/callout-release.js';
import { CalloutReleaseStatus } from '../features/payouts/CalloutReleaseStatus.js';
import { ApiError } from '../shared/api.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * `W4-T09` §2.3, §4.4 — Start work: the professional shows a code, and the client's own phone
 * approves it.
 *
 * **The code lives in this page's memory only.** It arrives as the issue fetcher's answer and is
 * never put in the URL, storage, the query cache or a log; the state this page polls never carries
 * it (`HandshakeStateSchema` is strict). Opening the page again shows Start work, and issuing again
 * supersedes whatever code was live (§2.2) — which is also how a refreshed screen recovers.
 *
 * The page is a 404 unless the caller holds an `AWARDED` quote on the job: the state read alone would
 * also admit the job's client, who would then be shown a button the API refuses.
 */
interface WorkJobData {
  locale: string;
  jobId: string;
  state: HandshakeState;
  /** `W5-T18` (E1): where the professional's share stands, read once the client approved. */
  release: CalloutReleaseView | null;
}

/** What Start work answers: the code to show, or a refusal as a key. */
type IssueActionData = { issued: HandshakeIssued } | { error: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const notFound = () => new Response('Not found', { status: 404 });

export async function loader({ params, context }: LoaderFunctionArgs): Promise<WorkJobData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const jobId = params['jobId'] ?? '';
  if (!UUID.test(jobId)) throw notFound();

  const awarded = await loadAwardedQuotes(context);
  if (!awarded.some((quote) => quote.jobId === jobId)) throw notFound();

  const { queryClient, api } = context.get(routeContext);
  try {
    // Fresh on every run: the poll is how this page learns the client approved, or the code lapsed.
    const state = await queryClient.fetchQuery({
      queryKey: queryKeys.handshake(jobId),
      queryFn: () => api.getHandshakeState(jobId),
      staleTime: 0,
    });
    const release = state.state === 'REDEEMED' ? await loadCalloutRelease(context, jobId) : null;
    return { locale, jobId, state, release };
  } catch (error) {
    // Not the provider of the job's live booking — one answer, as the API gives one (§4.2).
    if (error instanceof ApiError && error.status === 404) throw notFound();
    throw error;
  }
}

export async function action({ params, context }: ActionFunctionArgs): Promise<IssueActionData> {
  const jobId = params['jobId'] ?? '';
  const { api } = context.get(routeContext);
  try {
    return { issued: await api.issueHandshake(jobId) };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      throw redirect(`/${localeOf(params)}/login`);
    }
    return { error: handshakeRefusalKey(error) };
  }
}

/** Whole seconds until `expiresAt`, never below zero. */
function secondsLeft(expiresAt: string, now: number): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 1000));
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { locale, state, release } = useLoaderData<WorkJobData>();
  const issue = useFetcher<IssueActionData>();
  usePoll(HANDSHAKE_POLL_MS);

  const issued = issue.data !== undefined && 'issued' in issue.data ? issue.data.issued : null;
  const error = issue.data !== undefined && 'error' in issue.data ? issue.data.error : null;
  const redeemed = state.state === 'REDEEMED';

  const start = (): void => {
    void issue.submit({}, { method: 'post' });
  };

  /**
   * §2.3: at `EXPIRED` the screen re-issues by itself **while it is open** — and only for a code this
   * page asked for. `NONE` is a code that was burned or superseded elsewhere. Once per code: the read
   * after a re-issue is about the new one, and a server that kept reading a fresh code as lapsed would
   * otherwise turn this into a loop.
   */
  const lapsed = issued !== null && (state.state === 'EXPIRED' || state.state === 'NONE');
  const reissuedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!lapsed || issue.state !== 'idle' || reissuedFor.current === issued.handshakeId) return;
    reissuedFor.current = issued.handshakeId;
    void issue.submit({}, { method: 'post' });
  }, [lapsed, issued, issue]);

  // The countdown's clock. Presentation only: the server decides expiry, and the poll reports it.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (issued === null || redeemed) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [issued, redeemed]);

  return (
    <article data-testid="work-job">
      <p>
        <Link to={`/${locale}/work`}>{t('handshake.work.back')}</Link>
      </p>
      <h1>{t('handshake.pro.start')}</h1>

      {redeemed ? (
        <>
          <Alert tone="success">
            <span data-testid="handshake-approved">{t('handshake.pro.approved')}</span>
          </Alert>
          <CalloutReleaseStatus release={release} locale={locale} />
        </>
      ) : issued !== null ? (
        <section className="mp-section" data-testid="handshake-code" aria-live="polite">
          <p className="mp-lead">{t('handshake.pro.instructions')}</p>
          <HandshakeQr value={issued.url} label={t('handshake.pro.qrLabel')} />
          <p>
            {t('handshake.pro.shortCodeLabel')}{' '}
            <strong data-testid="handshake-short-code">{issued.shortCode}</strong>
          </p>
          <p className="mp-meta" data-testid="handshake-countdown">
            {t('handshake.pro.expiresIn', { seconds: secondsLeft(issued.expiresAt, now) })}
          </p>
        </section>
      ) : (
        <p>
          <Button variant="primary" onPress={start} isDisabled={issue.state !== 'idle'}>
            {t('handshake.pro.start')}
          </Button>
        </p>
      )}

      {error === null || redeemed ? null : <Alert tone="error">{t(error)}</Alert>}
    </article>
  );
}
